import FireFill from "@/nav-localized/assets/header/nav/fire-fill.svg?react";
import { HeaderProps, MenuProps } from "../types";
import { Drawer } from "@blofin/blofin-ui";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import Hot from "../Hot";
import { useDispatch, useStoreState } from "../store/useStore";
import { activeRouteStyles, stylesText } from "../styles";
import H5Menu from "./H5Menu";
import { getCurrentPath } from "./getActiveClass";
import useNav from "@/nav-localized/hooks/useNav";
import Menu from "./Menu";
import OverflowMenu from "./OverflowMenu";
import useOverflowNav from "./hooks/useOverflowNav";
import clsx from "clsx";

const checkMore = (pages: MenuProps[]) => {
  return pages.filter((item) => {
    if (item.more) {
      // 支持数组和对象两种格式
      if (Array.isArray(item.more)) {
        // 数组格式：检查是否有任何列包含菜单项
        return item.more.some((column) => Object.values(column).some((items) => items.length > 0));
      } else {
        // 对象格式：检查是否有任何分组包含菜单项
        return Object.values(item.more).some((items) => items.length > 0);
      }
    }
    return true;
  });
};

const Nav: FC<HeaderProps> = (props) => {
  const t = useTranslations("header");

  const { drawerOpen, theme, hideAcitvityLogo, isDemoTrading, localeLink } = useStoreState();

  const { dispatch } = useDispatch();

  const { changeRoute } = props;

  const { navs, pinnedTail = [] } = useNav(theme);

  const [pages, setPages] = useState<MenuProps[]>(isDemoTrading ? [] : navs);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setPages(checkMore(navs));
  }, [navs]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const { navRef, visibleCount, isMeasuring, hasMeasured } = useOverflowNav(pages.length);

  const visiblePages = useMemo(
    () => (isMeasuring ? pages : pages.slice(0, visibleCount)),
    [pages, visibleCount, isMeasuring]
  );
  const overflowPages = useMemo(
    () => (isMeasuring ? [] : pages.slice(visibleCount)),
    [pages, visibleCount, isMeasuring]
  );
  const hasOverflow = overflowPages.length > 0;

  const changeRouter = (item: MenuProps, e: React.MouseEvent<HTMLAnchorElement>) => {
    window?.reportTrack?.("HeadingBar_Click", {
      tab_name: item.label
    });
    if (item.clickFunc) {
      item.clickFunc(e);
    } else if (changeRoute) {
      e.preventDefault();
      if (!item.items && item.basePath) {
        changeRoute(`/${item.basePath}${localeLink}/${item.url}`);
      } else {
        changeRoute(`${localeLink}/${item.url}`);
      }
    }
  };

  const changePages = (label: string) => {
    setPages((pre) => {
      return checkMore(pre).map((item) => {
        if (item.label === label) {
          return {
            ...item,
            drawer: !item.drawer
          };
        }
        return item;
      });
    });
  };

  return (
    <div className="min-w-0">
      <ul
        ref={navRef}
        className={clsx(
          "min-[860px]:flex min-[860px]:items-center max-[860px]:hidden",
          isMeasuring ? "!invisible !absolute" : !hasMeasured && "overflow-hidden"
        )}
      >
        {(isMeasuring ? pages : visiblePages).map((item, index) => {
          return (
            <li
              key={index}
              data-nav-item
              className={clsx("ml-[24px] h-[56px] flex items-center relative")}
            >
              {item.items ? (
                <Menu menus={item} changeRouter={changeRouter} />
              ) : item.more ? (
                <Menu menus={item} itemsKey="more" changeRouter={changeRouter} />
              ) : (
                <a
                  href={`${item.url}`}
                  className="flex items-center gap-[2px]"
                  onClick={(e) => {
                    changeRouter(item, e);
                  }}
                >
                  {item.recommend && <FireFill className="w-4 h-4 shrink-0" />}
                  <span
                    className={clsx(
                      "text-[14px] flex items-center font-normal whitespace-nowrap",
                      isClient &&
                        getCurrentPath(item.activeRouts) &&
                        activeRouteStyles({ mode: theme }),
                      stylesText({ mode: theme }),
                      "hover:text-light-primary"
                    )}
                  >
                    {item.label && t(item.label)}
                    {item.hot && <Hot theme={theme} />}
                    {!hideAcitvityLogo && item.type === "img" && item.src}
                  </span>
                </a>
              )}
            </li>
          );
        })}
        {hasOverflow && (
          <li data-nav-overflow>
            <OverflowMenu items={overflowPages} changeRouter={changeRouter} />
          </li>
        )}
        {!hideAcitvityLogo &&
          pinnedTail.map((item, index) => (
            <li
              key={`pinned-${index}`}
              data-nav-pinned
              className="ml-[24px] h-[64px] flex items-center relative shrink-0"
            >
              <a
                href={`${item.url}`}
                onClick={(e) => {
                  changeRouter(item, e);
                }}
                className="flex items-center"
              >
                {item.type === "img" && item.src}
              </a>
            </li>
          ))}
      </ul>
      <Drawer
        title=""
        placement="right"
        theme={theme}
        content={
          <div>
            <ul className="flex flex-col">
              {pages.map((item, index) => {
                return (
                  <li
                    key={index}
                    className={`${
                      item.items ? " mb-[24px]" : " mb-[32px]"
                    } min-h-[24px] flex items-center relative`}
                  >
                    {item.items || item.more ? (
                      <H5Menu menus={item} changeRouter={changeRouter} changePages={changePages} />
                    ) : (
                      <a
                        href={`${item.url}`}
                        onClick={(e) => {
                          changeRouter(item, e);
                        }}
                      >
                        <span
                          className={`text-[16px] flex items-center font-normal ${stylesText({
                            mode: theme
                          })}`}
                        >
                          {item.icon && <item.icon className={stylesText({ mode: theme })} />}
                          {item.label !== "" && (
                            <i className="ml-[12px] not-italic"> {t(item.label)}</i>
                          )}
                        </span>
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        }
        drawerContentClass="w-[376px] max-[600px]:w-full"
        open={drawerOpen}
        cancel={() => dispatch({ type: "SET_DRAWEROPEN", payload: false })}
      />
    </div>
  );
};

export default Nav;
